import Image from "next/image";
import { Link } from "@/components/shared/inputs/link/link";
import type {
  WorkBlogMention,
  WorkCrossProductWidget,
  WorkTag,
} from "@/types/work.types";
import { sanitizeHTML } from "@/utility/html.utility";
import { formatWorkPrice } from "../work-price";
import styles from "./work-additional-content.module.scss";

const getCrossProductPrice = (
  item: WorkCrossProductWidget["items"][number],
) => {
  const hasPriceRange = item.minPrice !== item.maxPrice;
  const price =
    item.isCollectible && hasPriceRange
      ? `${formatWorkPrice(item.minPrice)} – ${formatWorkPrice(item.maxPrice)}`
      : formatWorkPrice(item.minPrice);

  return {
    prefix: hasPriceRange && !item.isCollectible ? "from: " : "",
    price,
  };
};

const sanitizeBlogPreview = (html: string): string =>
  sanitizeHTML(html, {
    whiteList: {
      a: ["href", "title"],
      b: [],
      blockquote: [],
      br: [],
      code: [],
      div: [],
      em: [],
      h1: [],
      h2: [],
      h3: [],
      h4: [],
      h5: [],
      h6: [],
      i: [],
      li: [],
      ol: [],
      p: [],
      pre: [],
      span: [],
      strong: [],
      ul: [],
    },
    stripIgnoreTag: true,
    stripIgnoreTagBody: ["script", "style"],
  });

export const CrossProductWidget = ({
  widget,
}: {
  widget: WorkCrossProductWidget | null;
}) => {
  if (!widget?.items.length) return null;

  return (
    <section
      className={styles.crossProduct}
      aria-labelledby="cross-product-title"
    >
      <h2 id="cross-product-title" className="u-title-sm">
        {widget.title}
      </h2>
      <div className={styles.crossProductGrid}>
        {widget.items.map((item) => {
          const displayPrice = getCrossProductPrice(item);

          return (
            <Link
              key={`${item.idWork}-${item.isCollectible}`}
              href={item.url}
              linkType="legacy"
              className={styles.crossProductItem}
            >
              <Image
                src={item.imageUrl}
                alt={`${item.title} image`}
                width={84}
                height={126}
                sizes="84px"
              />
              <span className={styles.crossProductText}>
                {item.blurb && <strong>{item.blurb}</strong>}
                <span className={styles.crossProductItemTitle}>
                  {item.title}
                </span>
                <small>
                  {displayPrice.prefix}
                  <b>{displayPrice.price}</b>
                </small>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export const RelatedSubjects = ({ subjects }: { subjects: WorkTag[] }) => {
  if (!subjects.length) return null;

  return (
    <section
      className={styles.subjects}
      aria-labelledby="related-subjects-title"
    >
      <h2 id="related-subjects-title">Related Subjects</h2>
      <div>
        {subjects.map((subject) => (
          <span
            key={`${subject.name}-${subject.url}`}
            className={styles.subjectTag}
          >
            <Link
              href={subject.url}
              linkType="legacy"
              rel={subject.noFollow ? "nofollow" : undefined}
            >
              {subject.name}
            </Link>
          </span>
        ))}
      </div>
    </section>
  );
};

export const BlogMentions = ({
  workTitle,
  mentions,
}: {
  workTitle: string;
  mentions: WorkBlogMention[];
}) => {
  if (!mentions.length) return null;

  return (
    <section className={styles.blog} aria-labelledby="blog-mentions-title">
      <h2 id="blog-mentions-title">{workTitle} Mentions in Our Blog</h2>
      <div className={styles.blogList}>
        {mentions.map((mention) => (
          <article key={mention.idBlogPage}>
            <Link
              href={mention.url}
              linkType="legacy"
              className={styles.blogImage}
              ariaLabel={mention.title}
            >
              <Image
                src={mention.thumbnailImageUrl}
                alt=""
                width={260}
                height={220}
                sizes="(max-width: 599px) 120px, 260px"
              />
            </Link>
            <div>
              <h3>
                <Link href={mention.url} linkType="legacy">
                  {mention.title}
                </Link>
              </h3>
              <p className={styles.blogMeta}>
                <span>Published by {mention.authorName}</span>
                <span className={styles.blogMetaBullet} aria-hidden>
                  •
                </span>
                <span>
                  {new Date(mention.postedAt).toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    timeZone: "UTC",
                  })}
                </span>
              </p>
              {mention.preview && (
                <div
                  className={styles.blogPreview}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeBlogPreview(mention.preview),
                  }}
                />
              )}
              <Link
                href={mention.url}
                linkType="legacy"
                className={styles.readArticle}
              >
                Read Article
              </Link>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
